import { useMemo } from 'react';
import {
  InsightsFilter,
  getAllMetricsAndAttributesForPlatform,
} from '@magicbrief/common';
import { ComboBox, Text } from 'react-aria-components';
import { useFormContext } from 'react-hook-form';
import {
  ComboBoxButton,
  ComboBoxContent,
  ComboBoxInput,
  ComboBoxItem,
} from '@magicbrief/ui/src/components/combo-box';
import { useParams } from 'react-router-dom';
import {
  getMetricLabelAsText,
  parseValueForMetric,
} from 'src/pages/Insights/util/useParseMetric';
import {
  useInsightsAdAccount,
  useInsightsAdsReferenceMetrics,
} from 'src/pages/Insights/util/useInsightsQueries';

type InsightsFormFilterMetricFieldSelectProps = {
  prefix: string;
  platform: 'facebook' | 'tiktok';
  onChange: (field: string) => void;
  allowedBuiltInMetrics?: string[];
  customEvents: string[] | null;
  customConversions: Array<{ facebookId: string; name: string }> | null;
};

export const InsightsFormFilterMetricFieldSelect: React.FC<
  InsightsFormFilterMetricFieldSelectProps
> = ({
  onChange,
  customEvents,
  customConversions,
  allowedBuiltInMetrics,
  platform,
  prefix,
}) => {
  const { watch } = useFormContext();
  const rootValue: InsightsFilter = watch(prefix);

  return (
    <InsightsFilterMetricFieldSelect
      platform={platform}
      value={rootValue.field}
      allowedBuiltInMetrics={allowedBuiltInMetrics}
      customEvents={customEvents}
      customConversions={customConversions}
      onChange={onChange}
    />
  );
};

type InsightsFilterMetricFieldSelectProps = {
  value?: string;
  onChange: (field: string) => void;
  platform: 'facebook' | 'tiktok';
  allowedBuiltInMetrics?:
    | Array<InsightsFilter['field']>
    | Readonly<Array<InsightsFilter['field']>>;
  customEvents: string[] | null;
  customConversions: Array<{ facebookId: string; name: string }> | null;
};

export const InsightsFilterMetricFieldSelect: React.FC<
  InsightsFilterMetricFieldSelectProps
> = ({
  value,
  platform,
  onChange,
  customEvents,
  customConversions,
  allowedBuiltInMetrics,
}) => {
  const { accountUuid } = useParams();
  const { currency } = useInsightsAdAccount({ accountUuid });

  const allMetrics =
    allowedBuiltInMetrics ?? getAllMetricsAndAttributesForPlatform(platform);

  const items = useMemo(
    () => [
      ...allMetrics.map((x) => ({ id: x })),
      ...(customEvents?.flatMap((x) => [
        { id: `action:cost_per:${x}` },
        { id: `action:total:${x}` },
        { id: `action:value:${x}` },
      ]) ?? []),
      ...(customConversions?.flatMap((x) => [
        { id: `action:cost_per:offsite_conversion.custom.${x.facebookId}` },
        { id: `action:total:offsite_conversion.custom.${x.facebookId}` },
        { id: `action:value:offsite_conversion.custom.${x.facebookId}` },
      ]) ?? []),
    ],
    [allMetrics, customEvents, customConversions]
  );

  const referenceMetrics = useInsightsAdsReferenceMetrics();

  return (
    <ComboBox
      className="flex-1"
      defaultSelectedKey={value}
      onSelectionChange={(selection) => onChange(selection as string)}
      aria-label="select metric"
    >
      <div className="relative">
        <ComboBoxInput className="pr-8" />
        <ComboBoxButton className="absolute right-1 top-1/2 -translate-y-1/2" />
      </div>
      <ComboBoxContent
        className="max-h-[250px]"
        popoverClassName="w-[500px] max-h-[100px]"
        items={items.map((m) => ({
          id: m.id,
          name: getMetricLabelAsText(
            platform,
            m.id,
            customEvents ?? [],
            customConversions ?? []
          ),
        }))}
      >
        {(item) => {
          const referenceMetric =
            referenceMetrics[item.id] &&
            currency &&
            item.id &&
            !Array.isArray(referenceMetrics[item.id])
              ? parseValueForMetric(
                  platform,
                  item.id,
                  referenceMetrics[item.id] as string | number | Date,
                  currency
                )
              : '-';

          return (
            <ComboBoxItem
              key={`${item.name}-${item.id}`}
              id={item.id}
              textValue={item.name || ''}
              className="justify-between data-[selected]:pr-8"
            >
              <Text>{item.name}</Text>
              <Text className="text-xs text-primary/50">{referenceMetric}</Text>
            </ComboBoxItem>
          );
        }}
      </ComboBoxContent>
    </ComboBox>
  );
};
